import React from 'react';
import { Card } from '@/components/gluestack/card';
import { VStack } from '@/components/gluestack/vstack';
import { HStack } from '@/components/gluestack/hstack';
import { Heading } from '@/components/gluestack/heading';
import { Text } from '@/components/gluestack/text';
import { useTranslation } from 'react-i18next';
import dayjs from '@/i18n/dayjs-config';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { StarAward01Icon } from '@hugeicons-pro/core-solid-rounded';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';

interface IPremiumStatusCardProps {
  premiumEndsOnUtc: Date;
  premiumPlanName: string;
  /** Icon inline with the title instead of in its own column (narrow spots like the right panel) */
  compact?: boolean;
}

export const PremiumStatusCard: React.FC<IPremiumStatusCardProps> = ({
  premiumEndsOnUtc,
  premiumPlanName,
  compact = false,
}) => {
  const { t } = useTranslation();
  const isLargeScreen = useIsLargeScreen();
  const hasIconColumn = isLargeScreen && !compact;

  const daysRemaining = dayjs(premiumEndsOnUtc).diff(dayjs(), 'day');
  const hoursRemaining = Math.max(0, Math.ceil(dayjs(premiumEndsOnUtc).diff(dayjs(), 'hour')));
  const showHours = daysRemaining < 3;

  const dateTime = dayjs(premiumEndsOnUtc).format('LLL');
  const timeRemaining = showHours
    ? t('account.plans.hoursLeft', { count: hoursRemaining })
    : t('account.plans.daysLeft', { count: daysRemaining });

  return (
    <Card className="border border-l-4 border-outline-100 border-l-success-500" size="md">
      <HStack space="md" className="items-start">
        {hasIconColumn && <HugeIconsWrapper icon={StarAward01Icon} size={36} className="text-success-500" />}
        <VStack space="sm" className="flex-1">
          <HStack space="sm" className="items-center">
            {!hasIconColumn && <HugeIconsWrapper icon={StarAward01Icon} size={24} className="text-success-500" />}
            <Heading size="sm" className="flex-1 text-typography-900 leading-6">
              {t('account.plans.yourAccountIsOn', { planName: premiumPlanName })}
            </Heading>
          </HStack>
          <VStack space="xs">
            <Text size="sm" className="text-typography-500">
              {t('account.plans.currentCreditsUntil')}
            </Text>
            <HStack className="flex-wrap items-baseline gap-x-3 gap-y-1">
              <Text size="lg" bold className="text-typography-900">
                {dateTime}
              </Text>
              <Text size="sm" bold className="text-success-600">
                {timeRemaining}
              </Text>
            </HStack>
          </VStack>
          <Text size="sm" className="text-typography-500 pt-1 leading-5">
            {t('account.plans.subscriptionRenewalNote', { planName: premiumPlanName })}
          </Text>
        </VStack>
      </HStack>
    </Card>
  );
};

